import React, { useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import { useAppContext } from "../context/AppContext";
import { createKPI, getAllUoMs, KPI_TYPE_LABELS } from "../services/kpiService";
import { toast } from "../utils/toast";
import { resolveRoleName } from "../utils/auth";

const KPICreatePage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    kpiName: "",
    description: "",
    unitOfMeasure: "",
    kpiType: "TYPE1",
  });

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const uomData = await getAllUoMs();
        const list = Array.isArray(uomData) ? uomData : [];
        setUoms(list);
        if (list.length > 0) {
          setField("unitOfMeasure", list[0].id);
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load units of measure");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const errors = useMemo(() => {
    const e = {};
    if (!String(form.kpiName).trim()) e.kpiName = "KPI Name is required";
    if (!form.unitOfMeasure) e.unitOfMeasure = "Unit of Measure is required";
    return e;
  }, [form]);

  const canSave = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const onSave = async () => {
    if (!canSave || saving) return;
    try {
      setSaving(true);
      await createKPI(form);
      toast.success("KPI created successfully");
      setActiveSection("kpi-management");
    } catch (e) {
      toast.error(e?.message || "Failed to create KPI");
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => setActiveSection("kpi-management");

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Create KPI</h2>
            <p className="page-subtitle">Add a new Key Performance Indicator</p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={onCancel}>
              Cancel
            </button>
            <button
              className="primary-btn"
              onClick={onSave}
              disabled={!canSave || saving}
            >
              {saving ? "Saving..." : "Create"}
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="no-results">Loading...</div>
        ) : (
          <div className="role-card">
            <div
              className="section-title"
              style={{ marginBottom: 12, fontWeight: 600 }}
            >
              KPI Details
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>KPI Name *</label>
                <input
                  value={form.kpiName}
                  onChange={(e) => setField("kpiName", e.target.value)}
                  placeholder="Enter KPI name"
                />
                {errors.kpiName && (
                  <div
                    className="input-hint"
                    style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}
                  >
                    {errors.kpiName}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>KPI Type *</label>
                <select
                  value={form.kpiType}
                  onChange={(e) => setField("kpiType", e.target.value)}
                >
                  {Object.entries(KPI_TYPE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Unit of Measure *</label>
                <select
                  value={form.unitOfMeasure}
                  onChange={(e) => setField("unitOfMeasure", e.target.value)}
                >
                  <option value="">Select UoM</option>
                  {uoms.map((uom) => (
                    <option key={uom.id} value={uom.id}>
                      {uom.uomValue}
                      {uom.description ? ` - ${uom.description}` : ""}
                    </option>
                  ))}
                </select>
                {errors.unitOfMeasure && (
                  <div
                    className="input-hint"
                    style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}
                  >
                    {errors.unitOfMeasure}
                  </div>
                )}
                {uoms.length === 0 && (
                  <div
                    className="input-hint"
                    style={{ fontSize: 12, marginTop: 4 }}
                  >
                    No UoMs available.{" "}
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => setActiveSection("uom-management")}
                    >
                      Create one first
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  placeholder="Enter description (optional)"
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KPICreatePage;
